import type { FontData } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { TriangleAlertIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { loadFont } from '../../../lib/font-loader'
import { Tooltip } from '../../rapid-ui'
import { ConfirmationDialog, type ConfirmationDialogRef } from '../../rapid-ui/structural'
import { logger } from '../../utilities'

export interface PendingFontUpload {
	tempId: string
	fileName: string
	status: 'uploading' | 'error'
	error?: string
}

interface FontCardProps {
	font: FontData
	mode: 'static' | 'picker' | 'page'
	isSelected: boolean
	allowDelete: boolean
	hasDuplicateName?: boolean
	onSelect: () => void
	onDelete?: () => void
}

export function FontCard({
	font,
	mode,
	isSelected,
	allowDelete,
	hasDuplicateName,
	onSelect,
	onDelete,
}: FontCardProps) {
	const deleteDialogRef = useRef<ConfirmationDialogRef>(null)
	const [fontLoaded, setFontLoaded] = useState(false)

	useEffect(() => {
		loadFont(font.displayName, font.url, font.weight, font.style)
			.then(() => setFontLoaded(true))
			.catch((error) => {
				logger.error('Failed to load font for preview', {
					category: 'fonts',
					data: { name: font.displayName, url: font.url, cause: error },
				})
			})
	}, [font.displayName, font.url, font.weight, font.style])

	const handleDeleteClick = async (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		try {
			await deleteDialogRef.current?.confirm({
				title: 'Delete font',
				message:
					`Are you sure you want to delete "${font.displayName}"? Canvases using this font will fall back to the system font.`,
				confirmText: 'Delete',
				cancelText: 'Cancel',
			})
			onDelete?.()
		} catch {
			// User cancelled
		}
	}

	const weightLabel = {
		'100': 'Thin',
		'200': 'Extra Light',
		'300': 'Light',
		'400': 'Regular',
		'500': 'Medium',
		'600': 'Semi Bold',
		'700': 'Bold',
		'800': 'Extra Bold',
		'900': 'Black',
	}[font.weight] ?? font.weight

	return (
		<>
			<div
				className={ctl(`
					flex flex-col gap-2 p-4
					rounded-sm bg-white ring ring-grey-200/20
					shadow-sm
					${
					isSelected
						? '-translate-y-[0.25rem] ring-2 ring-blue-500 bg-blue-50 shadow-md shadow-blue-500/70'
						: ''
				}
					transition-all
					${mode === 'picker' ? 'cursor-pointer' : ''}
				`)}
				onClick={mode === 'picker' ? onSelect : undefined}
				{...(mode === 'picker'
					? {
						role: 'button',
						tabIndex: 0,
						'aria-selected': isSelected,
						onKeyDown: (e: React.KeyboardEvent) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								onSelect()
							}
						},
					}
					: {})}
			>
				<div
					className="text-2xl truncate transition-opacity"
					style={{
						fontFamily: fontLoaded ? `"${font.displayName}", sans-serif` : undefined,
						fontWeight: Number(font.weight),
						fontStyle: font.style,
						opacity: fontLoaded ? 1 : 0.4,
					}}
				>
					{font.displayName}
				</div>
				<div className="text-[9px] text-grey-600 leading-snug">{font.displayName}</div>
				<div className="text-xs text-grey-500 flex items-center gap-2">
					<span>{weightLabel}</span>
					{font.style !== 'normal' && (
						<>
							<span className="text-grey-300">&bull;</span>
							<span className="capitalize">{font.style}</span>
						</>
					)}
					<span className="text-grey-300">&bull;</span>
					<span className="uppercase">{font.mimeType?.split('/').pop() ?? 'font'}</span>
					{hasDuplicateName && (
						<Tooltip content="Duplicate font name; only one will work. Remove one of the duplicates.">
							<TriangleAlertIcon className="size-3.5 text-amber-500" />
						</Tooltip>
					)}
				</div>

				{allowDelete && (
					<div className="flex justify-end">
						<button
							type="button"
							onClick={handleDeleteClick}
							className={ctl(`
								flex items-center justify-center
								px-2 py-1 rounded-md text-xs
								text-grey-500 hover:text-red-600 hover:bg-red-50
								transition-all cursor-pointer
								appearance-none border-none
							`)}
						>
							Delete
						</button>
					</div>
				)}
			</div>
			<ConfirmationDialog ref={deleteDialogRef} />
		</>
	)
}

export function FontCardPlaceholder({
	upload,
	onDismiss,
}: {
	upload: PendingFontUpload
	onDismiss?: () => void
}) {
	const isError = upload.status === 'error'
	const displayName = upload.fileName.replace(/\.[^.]+$/, '')

	return (
		<div
			className={ctl(`
				flex flex-col gap-2 p-4
				rounded-sm ring
				shadow-sm
				${
				isError
					? 'bg-red-50 ring-red-200'
					: 'bg-grey-50 ring-grey-200/20 animate-pulse'
			}
			`)}
		>
			<div className="text-2xl truncate text-grey-400">
				{displayName}
			</div>
			<div className="text-xs flex items-center gap-2">
				{isError
					? <span className="text-red-500">{upload.error ?? 'Upload failed'}</span>
					: <span className="text-grey-400">Uploading…</span>}
			</div>
			{isError && onDismiss && (
				<div className="flex justify-end">
					<button
						type="button"
						onClick={onDismiss}
						className={ctl(`
							flex items-center justify-center
							px-2 py-1 rounded-md text-xs
							text-grey-500 hover:text-grey-700 hover:bg-grey-100
							transition-all cursor-pointer
							appearance-none border-none
						`)}
					>
						Dismiss
					</button>
				</div>
			)}
		</div>
	)
}
